import React from 'react'
import { cn } from '@amusnet/react-ui'
import { ViewportType } from 'game-ui-types'
import { UltraPlaySpin, UltraPlaySubSpin, UltraPlaySpinType, useUltraPlayStore } from '@stores'
import { useConfigurationStore } from '@stores'
import { formatCurrency } from '@amusnet/amusnet-common'
import { playUltraPlaySound, ULTRA_PLAY_SOUND } from './ultraPlaySound'

// Recolourable star rendered inline so its fill follows the row state (black on the
// trigger circle, yellow on the free spins).
const StarIcon: React.FC<{ color: string; size: number }> = ({ color, size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="block shrink-0">
        <path
            d="M11.4946 2.7143C11.7012 2.29575 12.298 2.29575 12.5046 2.7143L15.1786 8.13242C15.2606 8.29863 15.4192 8.41383 15.6026 8.44048L21.5818 9.30932C22.0437 9.37643 22.2282 9.94406 21.8939 10.2698L17.5673 14.4873C17.4346 14.6166 17.374 14.803 17.4053 14.9857L18.4267 20.9408C18.5056 21.4008 18.0228 21.7516 17.6096 21.5345L12.2616 18.7228C12.0976 18.6366 11.9016 18.6366 11.7375 18.7228L6.38953 21.5345C5.9764 21.7516 5.49355 21.4008 5.57245 20.9408L6.59383 14.9857C6.62516 14.803 6.5646 14.6166 6.43187 14.4873L2.10525 10.2698C1.77102 9.94406 1.95545 9.37643 2.41734 9.30932L8.3966 8.44048C8.58001 8.41383 8.73857 8.29863 8.8206 8.13242L11.4946 2.7143Z"
            fill={color}
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
        />
    </svg>
)

// Shared row layout (fixed Figma sizes): SPIN/indicator | WIN amount | gap | multiplier | BALANCE.
// Columns are placed explicitly (win = col 2, multiplier = col 4) so the amount always ends and the
// multiplier always starts at the same x, regardless of their widths — and never overlap.
// Row grid: SPIN | WIN | gap | multiplier | BALANCE. The BALANCE column widens with the viewport
// (portrait 232 → landscape 365 → desktop 555). Full literal class strings so Tailwind detects them.
const ULTRA_PLAY_ROW_GRID_PORTRAIT = 'grid-cols-[1fr_212px_16px_100px_232px]'
const ULTRA_PLAY_ROW_GRID_LANDSCAPE = 'grid-cols-[1fr_212px_16px_100px_322px]'
const ULTRA_PLAY_ROW_GRID_DESKTOP = 'grid-cols-[1fr_212px_16px_100px_480px]'

export const getUltraPlayRowGrid = (viewport: ViewportType): string =>
    viewport === ViewportType.DESKTOP
        ? ULTRA_PLAY_ROW_GRID_DESKTOP
        : viewport === ViewportType.MOBILE_LANDSCAPE
          ? ULTRA_PLAY_ROW_GRID_LANDSCAPE
          : ULTRA_PLAY_ROW_GRID_PORTRAIT

// Sticky row, win multiplier ≥ 10x: the multiplier badge is outlined instead of filled.
// px-only, matching the non-sticky filled badge's padding — a vertical p-[5px] would grow the row's
// height while sticky and shrink it again once it drops down, a visible jump.
const STICKY_BIG_WIN_MULTIPLIER_CLASS = 'border-2 border-[#A6CE39] rounded-md px-[5px]'

// Win amount (col 2, right-aligned) + multiplier (col 4, left-aligned) as two fixed grid cells.
// Rendered as a fragment so the spans become direct children of the row grid. 10x+ gets a badge —
// filled everywhere except the sticky row, which gets an outlined variant instead (see STICKY_BIG_WIN_
// MULTIPLIER_CLASS).
const WinCell: React.FC<{
    win: number
    multiplier: number
    currency: string
    hideCents: boolean
    colorClass: string
    useOutlinedBadge?: boolean
}> = ({ win, multiplier, currency, hideCents, colorClass, useOutlinedBadge }) => {
    const multDisplay = multiplier % 1 === 0 ? `${multiplier}x` : `${multiplier.toFixed(2)}x`
    const isBigWin = win > 0 && multiplier >= 10

    return (
        <>
            <span
                className={cn(
                    'col-start-2 flex items-baseline justify-end whitespace-nowrap py-4 text-[24px] leading-[24px] font-bold',
                    colorClass
                )}
            >
                {win > 0 ? formatCurrency(win, { hideCents }) : '0.00'}{' '}
                <span className="text-[18px] ml-1">{currency}</span>
            </span>
            <span className="col-start-4 flex items-center justify-start py-4">
                <span
                    className={cn(
                        'text-[24px] font-bold leading-[24px]',
                        isBigWin
                            ? useOutlinedBadge
                                ? cn('text-white', STICKY_BIG_WIN_MULTIPLIER_CLASS)
                                : 'text-black bg-active rounded-md px-[5px]'
                            : win > 0
                              ? 'text-white'
                              : cn('opacity-70', colorClass)
                    )}
                >
                    {multDisplay}
                </span>
            </span>
        </>
    )
}

const WIN_DOT_CLASS = 'bg-ultra-win-dot border border-ultra-glow shadow-[0px_0px_4px_0px_#1BAE49]'

const NO_WIN_DOT_CLASS = 'bg-[#555555]'

export const SPECIAL_ROW_CLASS = 'border-2 border-ultra-accent shadow-[0px_6px_4px_0px_rgba(0,0,0,0.5)]'

// Sticky row, win multiplier > 1x: borrows the free-spins card's green glow instead of the plain
// accent-bordered SPECIAL_ROW_CLASS (used for a no-win or ≤1x sticky row).
export const STICKY_FEATURE_ROW_CLASS =
    'bg-[#082300] border-2 border-ultra-accent shadow-[0px_0px_40px_0px_#3CFF0066_inset]'

const BONUS_TYPES: UltraPlaySpinType[] = ['FREE_SPIN', 'CASCADE', 'BONUS']

type SubSpinRowProps = {
    subSpin: UltraPlaySubSpin
    currency: string
    hideCents: boolean
}

const SubSpinRow: React.FC<SubSpinRowProps> = ({ subSpin, currency, hideCents }) => {
    const isWin = subSpin.win > 0

    return (
        <div
            className={cn(
                'grid grid-cols-3 items-center px-[40px] py-[6px] rounded-xl',
                isWin ? 'bg-black' : 'bg-[#1a1a1a]'
            )}
        >
            <div className="flex items-center">
                <span className={cn('w-4 h-4 rounded-full shrink-0', isWin ? WIN_DOT_CLASS : NO_WIN_DOT_CLASS)} />
            </div>
            <div
                className={cn(
                    'text-center text-[22px] leading-[28px] font-bold',
                    isWin ? 'text-white' : 'text-ultra-header'
                )}
            >
                {subSpin.win > 0 ? formatCurrency(subSpin.win, { hideCents }) : '0.00'} {currency}
                {subSpin.winMultiplier > 0 && (
                    <span className="ml-2 opacity-70">
                        {subSpin.winMultiplier % 1 === 0
                            ? `${subSpin.winMultiplier}x`
                            : `${subSpin.winMultiplier.toFixed(2)}x`}
                    </span>
                )}
            </div>
            <div
                className={cn(
                    'text-right text-[22px] leading-[28px] font-bold',
                    isWin ? 'text-white' : 'text-ultra-header'
                )}
            >
                {formatCurrency(subSpin.balance, { hideCents })} {currency}
            </div>
        </div>
    )
}

type FreeSubSpinRowProps = {
    index: number
    subSpin: UltraPlaySubSpin
    bet: number // the triggering bet — the multiplier is win ÷ bet
    gridClass: string
    currency: string
    hideCents: boolean
    isSticky: boolean
    isLast: boolean // top of the list (final free spin) — closes the card/strip at the top
    isFirst: boolean // bottom of the list (first free spin) — closes the strip at the bottom
    showBalance: boolean
    balance: number
    bgClass: string
}

// Each free spin row sits inside the green card (#082300, 2px side borders, inset glow).
// The star sits on a continuous 0.5-opacity green strip; only the final (top) free spin
// rounds the strip/card top, only the first (bottom) free spin rounds the strip bottom.
const FreeSubSpinRow: React.FC<FreeSubSpinRowProps> = ({
    index,
    subSpin,
    bet,
    gridClass,
    currency,
    hideCents,
    isSticky,
    isLast,
    showBalance,
    balance,
    bgClass,
}) => {
    const isWin = subSpin.win > 0
    const textColorClass = isWin ? 'text-white' : 'text-ultra-header'
    const multiplier = bet > 0 ? subSpin.win / bet : 0

    return (
        <div
            className={cn(
                'relative grid items-stretch px-[40px]',
                gridClass,
                isSticky ? 'bg-[#082300]' : bgClass,
                isSticky && 'border-x-2 border-x-ultra-accent shadow-[0px_0px_40px_0px_#3CFF0066_inset]',
                isSticky && isLast && 'border-t-2 border-t-ultra-accent rounded-t-xl',
                isLast && 'pt-2'
            )}
        >
            <div className="col-start-1 flex items-center gap-2">
                <span className="absolute top-1/2 left-0 -translate-y-1/2 w-[40px] text-[24px] leading-[24px] font-bold text-center text-white">
                    {index}
                </span>
                <div
                    className={cn(
                        'flex items-center justify-center w-9 h-14 self-stretch bg-[rgba(103,204,40,0.5)]',
                        isSticky && 'border-x border-x-ultra-glow',
                        isSticky && isLast && 'border-t border-t-ultra-glow',
                        isLast && 'rounded-t-[36px]',
                        !isSticky && isLast && 'h-14'
                    )}
                >
                    <StarIcon color="#FFD100" size={24} />
                </div>

                {subSpin.freeSpinsAwarded ? (
                    <span className="text-white font-bold text-[24px] ml-2">+{subSpin.freeSpinsAwarded}</span>
                ) : null}
            </div>

            <WinCell
                win={subSpin.win}
                multiplier={multiplier}
                currency={currency}
                hideCents={hideCents}
                colorClass={textColorClass}
            />

            <div
                className={cn(
                    'col-start-5 flex items-center justify-end py-4 text-[24px] leading-[24px] font-bold',
                    textColorClass
                )}
            >
                {showBalance ? formatCurrency(balance, currency, { hideCents }) : ''}
            </div>
        </div>
    )
}

type UltraPlaySpinRowProps = {
    spin: UltraPlaySpin
    isSticky: boolean
}

const UltraPlaySpinRow: React.FC<UltraPlaySpinRowProps> = ({ spin, isSticky }) => {
    const toggleSpinExpanded = useUltraPlayStore((s) => s.toggleSpinExpanded)
    const currency = useConfigurationStore((s) => s.currency)
    const hideCents = useConfigurationStore((s) => s.currencyHideCents)
    const gridClass = getUltraPlayRowGrid(useConfigurationStore((s) => s.viewport))

    const hasSubSpins = spin.subSpins && spin.subSpins.length > 0

    const handleToggle = () => {
        playUltraPlaySound(ULTRA_PLAY_SOUND.COLLAPSE_ROWS)
        toggleSpinExpanded(spin.spinNumber)
    }
    const isFreeSpin = spin.type === 'FREE_SPIN'
    const isBonus = BONUS_TYPES.includes(spin.type)
    const isWin = spin.win > 0
    const isHighlighted = isSticky || isWin || isBonus

    const dotClass = isWin || isBonus ? WIN_DOT_CLASS : NO_WIN_DOT_CLASS
    const textColorClass = isHighlighted ? 'text-white' : 'text-ultra-header'
    const bgClass = isHighlighted ? 'bg-black' : 'bg-[#2A2A2A]'

    // Sticky row only, and only above a 1x win: borrow the free-spins card's green glow instead of the
    // plain SPECIAL_ROW_CLASS border (a no-win or ≤1x sticky row keeps that as-is). Once the row is no
    // longer sticky none of this applies — it falls back to the plain win/no-win styling above.
    const isBigWin = isWin && spin.winMultiplier >= 10
    const useFeatureCardStyle = isSticky && isWin && spin.winMultiplier > 1

    if (isFreeSpin) {
        const subCount = spin.subSpins?.length ?? 0

        // The marker is dynamic: collapsed it summarises the whole feature (triggering spin + all free
        // spins); expanded it shows only the triggering spin's own win, since the free spins are listed
        // above it. Multipliers everywhere are win ÷ the triggering bet.
        const subWinSum = spin.subSpins?.reduce((sum, s) => sum + s.win, 0) ?? 0
        const markerWin = spin.isExpanded ? spin.win : spin.win + subWinSum
        const markerMultiplier = spin.bet > 0 ? markerWin / spin.bet : 0

        const finalBalance = spin.balance + subWinSum

        return (
            <div className="flex flex-col">
                {/* Free spins, newest on top (1..N bottom-to-top) */}
                {hasSubSpins &&
                    spin.isExpanded &&
                    [...spin.subSpins!]
                        .reverse()
                        .map((sub, j) => (
                            <FreeSubSpinRow
                                key={subCount - j}
                                index={subCount - j}
                                subSpin={sub}
                                bet={spin.bet}
                                gridClass={gridClass}
                                currency={currency}
                                hideCents={hideCents}
                                isSticky={isSticky}
                                isLast={j === 0}
                                isFirst={j === subCount - 1}
                                showBalance={j === 0}
                                balance={finalBalance}
                                bgClass={bgClass}
                            />
                        ))}

                {/* "+N" trigger marker — bottom of the card (keeps its own win/balance) */}
                <div
                    className={cn(
                        'grid items-center px-[40px] cursor-default',
                        gridClass,
                        isSticky ? 'bg-[#082300]' : bgClass,
                        isSticky &&
                            'border-x-2 border-b-2 border-ultra-accent shadow-[0px_0px_40px_0px_#3CFF0066_inset]',
                        (spin.isExpanded || isSticky) && 'rounded-b-xl'
                    )}
                    onClick={hasSubSpins ? handleToggle : undefined}
                >
                    <div className="col-start-1 flex items-center gap-2">
                        <div
                            className={cn(
                                'relative w-9 h-11 -mt-3',
                                (spin.isExpanded || isSticky) && 'bg-[rgba(103,204,40,0.5)] rounded-b-[36px]',
                                isSticky && 'border-x border-b border-ultra-glow'
                            )}
                        >
                            <div className="absolute -left-px w-9 h-9 mt-2 p-1.5 rounded-[27px] flex items-center justify-center shrink-0 bg-ultra-special-dot shadow-[0px_0px_4px_0px_#A6AE1B]">
                                <StarIcon color="#000000" size={24} />
                            </div>
                        </div>
                        {spin.freeSpinsAwarded !== undefined && (
                            <span className="text-white font-bold text-[24px]">+{spin.freeSpinsAwarded}</span>
                        )}
                    </div>

                    <WinCell
                        win={markerWin}
                        multiplier={markerMultiplier}
                        currency={currency}
                        hideCents={hideCents}
                        colorClass="text-white"
                    />

                    <div className="col-start-5 text-right text-[24px] leading-[24px] font-bold text-white">
                        {formatCurrency(spin.balance, { hideCents })}{' '}
                        <span className="text-[18px] ml-1">{currency}</span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div>
            <div
                className={cn(
                    'rounded-xl px-[40px] cursor-default',
                    useFeatureCardStyle ? STICKY_FEATURE_ROW_CLASS : bgClass,
                    isSticky && !useFeatureCardStyle && SPECIAL_ROW_CLASS
                )}
                onClick={hasSubSpins ? handleToggle : undefined}
            >
                <div className={cn('grid items-center', gridClass)}>
                    {/* Dot — far left */}
                    <div className="col-start-1 flex items-center gap-2">
                        {isBonus && spin.freeSpinsAwarded ? (
                            <span className="text-[#FFD700] font-bold text-[14px]">+{spin.freeSpinsAwarded}</span>
                        ) : (
                            <span className={cn('w-6 h-6 ml-1.5 rounded-full shrink-0', dotClass)} />
                        )}
                        {hasSubSpins && (
                            <span className="text-white/40 text-[12px]">{spin.isExpanded ? '▲' : '▼'}</span>
                        )}
                    </div>

                    {/* Win amount (col 2) + multiplier (col 4) — fixed columns, never shift or overlap */}
                    <WinCell
                        win={spin.win}
                        multiplier={spin.winMultiplier}
                        currency={currency}
                        hideCents={hideCents}
                        colorClass={textColorClass}
                        useOutlinedBadge={isSticky && isBigWin}
                    />

                    {/* Balance — far right */}
                    <div className={cn('col-start-5 text-right text-[24px] leading-[24px] font-bold', textColorClass)}>
                        {formatCurrency(spin.balance, { hideCents })}{' '}
                        <span className="text-[18px] ml-1">{currency}</span>
                    </div>
                </div>
            </div>

            {hasSubSpins && spin.isExpanded && (
                <div className="flex flex-col gap-[4px] mt-[4px]">
                    {spin.subSpins!.map((sub, i) => (
                        <SubSpinRow key={i} subSpin={sub} currency={currency} hideCents={hideCents} />
                    ))}
                </div>
            )}
        </div>
    )
}

export default UltraPlaySpinRow
